<template>
  <div class="pb-10">
    <!-- Edit Company Modal -->
    <EditCompanyModal
      :show="showEditModal"
      :company="company"
      @close="showEditModal = false"
      @company-updated="onCompanyUpdated"
    />

    <!-- Company Settings Modal -->
    <CompanySettingsModal
      :show="showSettingsModal"
      @close="showSettingsModal = false"
      @settings-updated="onSettingsUpdated"
    />

    <!-- Logo Upload Modal -->
    <transition name="modal">
      <div v-if="showLogoUploadModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click.self="closeLogoUploadModal">
        <div class="bg-white rounded-xl max-w-md w-full shadow-xl" @click.stop>
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h3 class="text-base font-semibold text-gray-900">Modifier le logo</h3>
            <button class="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-gray-500 border border-gray-200 rounded-lg transition-colors"
              @click="closeLogoUploadModal" :disabled="isUploadingLogo">
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>
          <div class="p-6 space-y-4">
            <!-- Aperçu -->
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-2">Aperçu</p>
              <div class="w-full h-32 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center bg-gray-50">
                <img v-if="logoPreview" :src="logoPreview" alt="Aperçu" class="max-h-28 max-w-full object-contain" />
                <img v-else-if="company?.logo_url" :src="company.logo_url" alt="Logo actuel" class="max-h-28 max-w-full object-contain" />
                <div v-else class="text-center">
                  <i class="fas fa-image text-gray-300 text-2xl"></i>
                  <p class="text-xs text-gray-400 mt-1">Aucun logo</p>
                </div>
              </div>
            </div>
            <!-- Fichier -->
            <div>
              <label class="cursor-pointer">
                <input type="file" ref="fileInput" accept="image/*" @change="handleFileSelect" class="hidden" :disabled="isUploadingLogo" />
                <div class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary border border-primary/30 hover:bg-primary/5 rounded-lg transition-colors">
                  <i class="fas fa-upload text-[10px]"></i>
                  {{ selectedFile ? 'Changer le fichier' : 'Choisir un fichier' }}
                </div>
              </label>
              <p class="text-[11px] text-gray-300 mt-1">Formats: JPG, PNG, GIF (max 2MB)</p>
            </div>
            <!-- Erreur -->
            <div v-if="logoUploadError" class="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-100 rounded-lg">
              <i class="fas fa-exclamation-circle text-red-500 text-xs flex-shrink-0"></i>
              <span class="text-xs text-red-600">{{ logoUploadError }}</span>
            </div>
          </div>
          <div class="flex gap-3 px-6 pb-6">
            <button class="flex-1 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              @click="closeLogoUploadModal" :disabled="isUploadingLogo">
              Annuler
            </button>
            <button
              class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="uploadLogo" :disabled="!selectedFile || isUploadingLogo">
              <i v-if="isUploadingLogo" class="fas fa-spinner fa-spin text-[10px]"></i>
              <span v-else>Enregistrer</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div>
        <h1 class="text-lg font-semibold text-gray-900">Paramètres</h1>
        <p class="text-xs text-gray-400">Configuration de votre entreprise</p>
      </div>
    </div>

    <div class="space-y-5">
      <!-- Carte Entreprise -->
      <div class="bg-white rounded-xl border border-gray-200">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-building text-primary text-sm"></i>
            </div>
            <div>
              <h3 class="text-sm font-semibold text-gray-700">Informations de l'entreprise</h3>
              <p class="text-xs text-gray-400">Détails et coordonnées de votre société</p>
            </div>
          </div>
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            @click="editCompany">
            <i class="fas fa-pen text-[10px]"></i>
            Modifier
          </button>
        </div>

        <!-- Chargement -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-16">
          <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
          <p class="text-sm text-gray-400">Chargement des informations...</p>
        </div>

        <!-- Vide -->
        <div v-else-if="!company" class="py-12 text-center">
          <div class="text-gray-300 mb-3">
            <i class="fas fa-circle-info text-3xl"></i>
          </div>
          <p class="text-sm font-medium text-gray-500">Aucune information</p>
          <p class="text-xs text-gray-400 mt-0.5">Les informations de l'entreprise ne sont pas disponibles</p>
        </div>

        <!-- Contenu -->
        <div v-else class="p-5">
          <!-- Profil -->
          <div class="flex items-center gap-4 mb-6">
            <div class="relative group">
              <div v-if="company.logo_url" class="w-16 h-16 rounded-xl overflow-hidden border border-gray-200">
                <img :src="company.logo_url" :alt="company.name" class="w-full h-full object-cover" />
              </div>
              <div v-else class="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                <span class="text-lg font-bold text-primary">{{ getInitials(company.name) }}</span>
              </div>
              <button
                class="absolute inset-0 w-full h-full rounded-xl bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                @click="openLogoUploadModal">
                <i class="fas fa-camera text-white text-sm"></i>
              </button>
            </div>
            <div>
              <h2 class="text-base font-bold text-gray-900">{{ company.name || 'Non défini' }}</h2>
              <p v-if="company.business_type" class="text-xs text-gray-400 mt-0.5">
                <i class="fas fa-tag mr-1 text-[10px]"></i>{{ company.business_type }}
              </p>
              <p class="text-xs text-gray-300 mt-0.5">
                <i class="fas fa-calendar mr-1 text-[10px]"></i>Créé le {{ formatDate(company.created_at) }}
              </p>
            </div>
          </div>

          <!-- Description -->
          <div v-if="company.description" class="mb-6">
            <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Description</p>
            <div class="bg-gray-50 rounded-lg p-3">
              <p class="text-sm text-gray-600 whitespace-pre-line">{{ company.description }}</p>
            </div>
          </div>

          <!-- Grille d'infos -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- Contact -->
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-3">
                <i class="fas fa-comment mr-1 text-[10px]"></i>Contact
              </p>
              <div class="space-y-2.5" v-if="company.phone || company.email || company.website">
                <div v-if="company.phone" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-phone text-blue-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Téléphone</p>
                    <p class="text-sm font-medium text-gray-900">{{ company.phone }}</p>
                  </div>
                </div>
                <div v-if="company.email" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-envelope text-green-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Email</p>
                    <p class="text-sm font-medium text-gray-900">{{ company.email }}</p>
                  </div>
                </div>
                <div v-if="company.website" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-globe text-purple-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Site web</p>
                    <p class="text-sm font-medium text-gray-900">{{ company.website }}</p>
                  </div>
                </div>
              </div>
              <p v-else class="text-xs text-gray-300 italic">Aucune information de contact</p>
            </div>

            <!-- Adresse -->
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-3">
                <i class="fas fa-map-marker-alt mr-1 text-[10px]"></i>Adresse
              </p>
              <div class="space-y-2.5" v-if="company.address || company.city || company.country">
                <div v-if="company.address" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-map-marker-alt text-orange-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Adresse</p>
                    <p class="text-sm font-medium text-gray-900">{{ company.address }}</p>
                  </div>
                </div>
                <div v-if="company.city" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-building text-amber-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Ville</p>
                    <p class="text-sm font-medium text-gray-900">{{ company.city }}</p>
                  </div>
                </div>
                <div v-if="company.country" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-globe text-teal-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Pays</p>
                    <p class="text-sm font-medium text-gray-900">{{ company.country }}</p>
                  </div>
                </div>
              </div>
              <p v-else class="text-xs text-gray-300 italic">Aucune adresse renseignée</p>
            </div>

            <!-- Infos légales -->
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-3">
                <i class="fas fa-file-alt mr-1 text-[10px]"></i>Informations légales
              </p>
              <div class="space-y-2.5" v-if="company.ninea || company.rccm || company.tax_id || company.capital">
                <div v-if="company.ninea" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-file-alt text-indigo-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">NINEA</p>
                    <p class="text-sm font-medium text-gray-900">{{ company.ninea }}</p>
                  </div>
                </div>
                <div v-if="company.rccm" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-file-alt text-indigo-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">RCCM</p>
                    <p class="text-sm font-medium text-gray-900">{{ company.rccm }}</p>
                  </div>
                </div>
                <div v-if="company.tax_id" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-credit-card text-red-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Numéro fiscal</p>
                    <p class="text-sm font-medium text-gray-900">{{ company.tax_id }}</p>
                  </div>
                </div>
                <div v-if="company.capital" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-dollar-sign text-green-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Capital</p>
                    <p class="text-sm font-medium text-gray-900">{{ formatCurrency(company.capital) }}</p>
                  </div>
                </div>
              </div>
              <p v-else class="text-xs text-gray-300 italic">Aucune information légale</p>
            </div>

            <!-- Config caisse -->
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-3">
                <i class="fas fa-cash-register mr-1 text-[10px]"></i>Configuration caisse
              </p>
              <div class="space-y-2.5" v-if="company.currency || company.cash_register_start_balance !== null && company.cash_register_start_balance !== undefined || company.max_cash_registers !== null && company.max_cash_registers !== undefined">
                <div v-if="company.currency" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-yellow-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-dollar-sign text-yellow-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Devise</p>
                    <p class="text-sm font-medium text-gray-900">{{ company.currency }}</p>
                  </div>
                </div>
                <div v-if="company.cash_register_start_balance !== null && company.cash_register_start_balance !== undefined" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-dollar-sign text-emerald-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Solde de départ caisse</p>
                    <p class="text-sm font-medium text-gray-900">{{ formatCurrency(company.cash_register_start_balance, company.currency) }}</p>
                  </div>
                </div>
                <div v-if="company.max_cash_registers !== null && company.max_cash_registers !== undefined" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-cash-register text-cyan-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Nombre max de caisses</p>
                    <p class="text-sm font-medium text-gray-900">{{ company.max_cash_registers || 'Illimité' }}</p>
                  </div>
                </div>
              </div>
              <p v-else class="text-xs text-gray-300 italic">Aucune configuration de caisse</p>
            </div>

            <!-- État de la licence -->
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-3">
                <i class="fas fa-id-card mr-1 text-[10px]"></i>État de la licence
              </p>
              <div class="space-y-2.5">
                <div class="flex items-center gap-3">
                  <div
                    class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    :class="licenseStatusIconClass"
                  >
                    <i class="fas text-[10px]" :class="licenseStatusIcon"></i>
                  </div>
                  <div class="min-w-0">
                    <p class="text-[11px] text-gray-400">Statut</p>
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                      :class="licenseStatusBadgeClass"
                    >
                      {{ licenseStatusLabel }}
                    </span>
                  </div>
                </div>
                <div v-if="activeLicense?.expires_at || activeLicense?.end_date" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-calendar text-gray-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Expiration</p>
                    <p class="text-sm font-medium text-gray-900">
                      {{ formatDate(activeLicense?.expires_at || activeLicense?.end_date) }}
                    </p>
                  </div>
                </div>
                <div v-if="isGracePeriod && activeLicense?.grace_end_date" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-ban text-amber-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Fin de la période de grâce (application bloquée)</p>
                    <p class="text-sm font-medium text-amber-800">
                      {{ formatDate(activeLicense.grace_end_date) }}
                    </p>
                    <p class="text-xs text-amber-700 mt-0.5">Passée cette date, l'application sera bloquée.</p>
                  </div>
                </div>
                <div v-if="activeLicense?.plan || activeLicense?.name" class="flex items-center gap-3">
                  <div class="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <i class="fas fa-tag text-gray-500 text-[10px]"></i>
                  </div>
                  <div>
                    <p class="text-[11px] text-gray-400">Formule</p>
                    <p class="text-sm font-medium text-gray-900">{{ activeLicense?.plan || activeLicense?.name || '—' }}</p>
                  </div>
                </div>
              </div>
              <p v-if="!activeLicense" class="text-xs text-gray-300 italic">Aucune licence enregistrée</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Configuration avancée -->
      <div class="bg-white rounded-xl border border-gray-200">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-cog text-gray-500 text-sm"></i>
            </div>
            <div>
              <h3 class="text-sm font-semibold text-gray-700">Configuration avancée</h3>
              <p class="text-xs text-gray-400">Paramètres de vente et gestion des caisses</p>
            </div>
          </div>
          <button
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            @click="openSettingsModal">
            <i class="fas fa-cog text-[10px]"></i>
            Configurer
          </button>
        </div>

        <div class="p-5 space-y-3">
          <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div class="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-shopping-cart text-blue-500 text-[11px]"></i>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-900">Paramètres de vente</p>
              <p class="text-xs text-gray-400">Autorisations et restrictions</p>
            </div>
          </div>
          <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <div class="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-cash-register text-purple-500 text-[11px]"></i>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-900">Gestion des caisses</p>
              <p class="text-xs text-gray-400">Limites et configuration</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import EditCompanyModal from './EditCompanyModal.vue'
import CompanySettingsModal from '@/components/settings/CompanySettingsModal.vue'
import { companyService } from '@/services'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'SettingsView',
  components: {
    EditCompanyModal,
    CompanySettingsModal
  },
  setup() {
    const toastStore = useToastStore()
    const company = ref(null)
    const loading = ref(true)
    const showEditModal = ref(false)
    const showSettingsModal = ref(false)
    const showLogoUploadModal = ref(false)
    const selectedFile = ref(null)
    const logoPreview = ref(null)
    const isUploadingLogo = ref(false)
    const logoUploadError = ref(null)
    const fileInput = ref(null)

    const loadCompanyFromStorage = () => {
      try {
        loading.value = true
        const stored = localStorage.getItem('company')
        if (stored) {
          company.value = JSON.parse(stored)
        }
      } catch (e) {
        console.error('Erreur lors du chargement des informations de l\'entreprise:', e)
        company.value = null
      } finally {
        loading.value = false
      }
    }

    // Licence : company.active_license ou localStorage (aligné avec MainLayout)
    const activeLicense = computed(() => {
      if (company.value?.active_license !== undefined && company.value?.active_license !== null) {
        return company.value.active_license
      }
      try {
        const stored = localStorage.getItem('company_active_licence')
        return stored ? JSON.parse(stored) : null
      } catch {
        return null
      }
    })

    const licenseStatusLabel = computed(() => {
      const lic = activeLicense.value
      if (!lic) return 'Aucune licence'
      const status = (lic.status ?? lic.is_active)
      if (status === true || status === 'active' || status === 'valid') return 'Actif'
      if (status === false || status === 'expired' || status === 'inactive') return 'Expiré'
      if (status === 'trial') return 'Essai'
      if (status === 'grace_period') return 'Phase de grâce'
      return typeof status === 'string' ? status : 'Inconnu'
    })

    const licenseStatusBadgeClass = computed(() => {
      const lic = activeLicense.value
      if (!lic) return 'bg-gray-100 text-gray-600'
      const status = (lic.status ?? lic.is_active)
      if (status === true || status === 'active' || status === 'valid') return 'bg-green-100 text-green-800'
      if (status === false || status === 'expired' || status === 'inactive') return 'bg-red-100 text-red-800'
      if (status === 'trial') return 'bg-amber-100 text-amber-800'
      if (status === 'grace_period') return 'bg-amber-100 text-amber-800'
      return 'bg-gray-100 text-gray-600'
    })

    const licenseStatusIcon = computed(() => {
      const lic = activeLicense.value
      if (!lic) return 'fa-id-card text-gray-400'
      const status = (lic.status ?? lic.is_active)
      if (status === true || status === 'active' || status === 'valid') return 'fa-check-circle text-green-500'
      if (status === false || status === 'expired' || status === 'inactive') return 'fa-times-circle text-red-500'
      if (status === 'trial') return 'fa-clock text-amber-500'
      if (status === 'grace_period') return 'fa-clock text-amber-500'
      return 'fa-id-card text-gray-400'
    })

    const licenseStatusIconClass = computed(() => {
      const lic = activeLicense.value
      if (!lic) return 'bg-gray-100'
      const status = (lic.status ?? lic.is_active)
      if (status === true || status === 'active' || status === 'valid') return 'bg-green-50'
      if (status === false || status === 'expired' || status === 'inactive') return 'bg-red-50'
      if (status === 'trial') return 'bg-amber-50'
      if (status === 'grace_period') return 'bg-amber-50'
      return 'bg-gray-100'
    })

    const isGracePeriod = computed(() => {
      const lic = activeLicense.value
      return lic && (lic.status ?? lic.is_active) === 'grace_period'
    })

    const editCompany = () => {
      showEditModal.value = true
    }

    const openSettingsModal = () => {
      showSettingsModal.value = true
    }

    const onCompanyUpdated = (updatedCompany) => {
      company.value = updatedCompany
    }

    const onSettingsUpdated = () => {
    }

    const openLogoUploadModal = () => {
      showLogoUploadModal.value = true
      logoUploadError.value = null
      selectedFile.value = null
      logoPreview.value = null
    }

    const closeLogoUploadModal = () => {
      if (isUploadingLogo.value) return
      showLogoUploadModal.value = false
      selectedFile.value = null
      logoPreview.value = null
      logoUploadError.value = null
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    const handleFileSelect = (event) => {
      const file = event.target.files?.[0]
      if (!file) return

      if (file.size > 2 * 1024 * 1024) {
        logoUploadError.value = 'Le fichier est trop volumineux. Taille maximale: 2MB'
        selectedFile.value = null
        logoPreview.value = null
        return
      }

      if (!file.type.startsWith('image/')) {
        logoUploadError.value = 'Le fichier doit être une image'
        selectedFile.value = null
        logoPreview.value = null
        return
      }

      logoUploadError.value = null
      selectedFile.value = file

      const reader = new FileReader()
      reader.onload = (e) => {
        logoPreview.value = e.target.result
      }
      reader.readAsDataURL(file)
    }

    const uploadLogo = async () => {
      if (!selectedFile.value || !company.value?.id) return

      try {
        isUploadingLogo.value = true
        logoUploadError.value = null

        const response = await companyService.uploadLogo(company.value.id, selectedFile.value)

        if (response.data?.company?.logo_url) {
          company.value.logo_url = response.data.company.logo_url
        } else if (response.data?.logo_url) {
          company.value.logo_url = response.data.logo_url
        }

        localStorage.setItem('company', JSON.stringify(company.value))

        toastStore.addToast({
          type: 'success',
          message: 'Logo mis à jour avec succès',
          duration: 4000
        })

        closeLogoUploadModal()
      } catch (error) {
        console.error('Erreur lors de l\'upload du logo:', error)
        logoUploadError.value = error.response?.data?.message || 'Erreur lors de l\'upload du logo'
        toastStore.addToast({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors de l\'upload du logo',
          duration: 4000
        })
      } finally {
        isUploadingLogo.value = false
      }
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: '2-digit'
      })
    }

    const formatCurrency = (amount, currency = 'XOF') => {
      const currencyLabels = {
        'XOF': 'FCFA',
        'EUR': '€',
        'USD': '$',
        'GBP': '£'
      }
      const label = currencyLabels[currency] || currency
      return new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount || 0) + ' ' + label
    }

    onMounted(() => {
      loadCompanyFromStorage()
    })

    return {
      company,
      loading,
      activeLicense,
      isGracePeriod,
      licenseStatusLabel,
      licenseStatusBadgeClass,
      licenseStatusIcon,
      licenseStatusIconClass,
      showEditModal,
      showSettingsModal,
      showLogoUploadModal,
      selectedFile,
      logoPreview,
      isUploadingLogo,
      logoUploadError,
      fileInput,
      editCompany,
      openSettingsModal,
      openLogoUploadModal,
      closeLogoUploadModal,
      handleFileSelect,
      uploadLogo,
      onCompanyUpdated,
      onSettingsUpdated,
      getInitials,
      formatDate,
      formatCurrency
    }
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
